import React, { useEffect, useMemo, useState } from "react"
import { Row, Col, Input, Label } from "reactstrap"
import { getDocumentTypes } from "../../services/admin"
import { toastError } from "../../helpers/msg_helper"
import UploadArrowIcon from "../../assets/icons/iconComponents/uploadArrowIcon"

/**
 * Configurable Documents section (StaffFlow parity).
 *
 * Renders every ACTIVE document type whose `masters` list includes `master`
 * (e.g. "supplier" / "customer"), with each type's configured fields
 * (text / number / date / select / file / textarea / boolean). The standard
 * doc types + their fields are defined under Developer → Documents Manager.
 *
 * State shape lifted to the parent via `onChange`:
 *   { [documentTypeId]: { [fieldKey]: value | File } }
 * — string values for text/number/date/select/textarea, boolean for boolean,
 *   and a File object (new upload) or a stored path string (existing) for file.
 * The parent serialises this as `documents` in its multipart submit payload.
 *
 * `value` (edit mode): the saved records, an array of
 *   [{ document_type_id, values: { fieldKey: storedValue } }]
 * used to prefill. `baseUrl` is prefixed to existing file paths for the link.
 */
const EntityDocuments = ({ master, value, onChange, baseUrl = "" }) => {
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [state, setState] = useState({})

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await getDocumentTypes()
        const list = (res?.data || []).filter(
          t => t.is_active && (t.masters || []).includes(master)
        )
        if (alive) setTypes(list)
      } catch (e) {
        toastError(e?.message || "Failed to load document types")
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [master])

  // prefill from saved records on edit
  useEffect(() => {
    if (Array.isArray(value) && value.length) {
      const init = {}
      value.forEach(r => {
        init[r.document_type_id] = { ...(r.values || {}) }
      })
      setState(init)
    }
  }, [value])

  const setField = (typeId, key, val) => {
    setState(prev => {
      const next = { ...prev, [typeId]: { ...(prev[typeId] || {}), [key]: val } }
      onChange && onChange(next)
      return next
    })
  }

  const empty = useMemo(() => !loading && types.length === 0, [loading, types])

  if (loading || empty) return null

  return (
    <div className="ev-docs mt-2">
      <h6 className="ev-section-title mb-1" style={{ fontWeight: 600 }}>
        Documents
      </h6>
      <p className="text-muted small mb-3">
        Identity &amp; regulatory documents. Configured in Developer → Documents Manager.
      </p>

      {types.map(t => (
        <div key={t.id} className="ev-doc-block">
          <div className="ev-doc-name">{t.name}</div>
          {t.description ? (
            <div className="text-muted small mb-2">{t.description}</div>
          ) : null}
          <Row>
            {(t.fields || []).map(f => {
              const v = state[t.id]?.[f.key]
              const labelEl = (
                <Label className="dm-label">
                  {f.label}
                  {f.is_required ? " *" : ""}
                  {f.is_expiry ? " (expiry)" : ""}
                </Label>
              )
              return (
                <Col
                  md={f.type === "textarea" || f.type === "file" ? 6 : 4}
                  xs={12}
                  key={f.key}
                  className="mb-3"
                >
                  {f.type !== "boolean" && labelEl}
                  {renderControl(t.id, f, v, setField, baseUrl)}
                  {f.help ? (
                    <div className="text-muted small mt-1">{f.help}</div>
                  ) : null}
                </Col>
              )
            })}
          </Row>
        </div>
      ))}
    </div>
  )
}

function renderControl(typeId, f, v, setField, baseUrl) {
  switch (f.type) {
    case "select":
      return (
        <Input
          type="select"
          value={v ?? ""}
          onChange={e => setField(typeId, f.key, e.target.value)}
        >
          <option value="">Select</option>
          {(f.options || []).map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Input>
      )
    case "date":
      return (
        <Input
          type="date"
          value={v ?? ""}
          onChange={e => setField(typeId, f.key, e.target.value)}
        />
      )
    case "number":
      return (
        <Input
          type="number"
          value={v ?? ""}
          onChange={e => setField(typeId, f.key, e.target.value)}
        />
      )
    case "textarea":
      return (
        <Input
          type="textarea"
          rows={2}
          value={v ?? ""}
          onChange={e => setField(typeId, f.key, e.target.value)}
        />
      )
    case "boolean":
      return (
        <div className="form-check mt-4">
          <Input
            type="checkbox"
            id={`doc-${typeId}-${f.key}`}
            checked={!!v}
            onChange={e => setField(typeId, f.key, e.target.checked)}
          />
          <Label check for={`doc-${typeId}-${f.key}`}>
            {f.label}
            {f.is_required ? " *" : ""}
          </Label>
        </div>
      )
    case "file": {
      const isFile = v instanceof File
      const name = isFile ? v.name : typeof v === "string" && v ? v.split("/").pop() : ""
      const accept = (f.file_types || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => (s.startsWith(".") ? s : `.${s}`))
        .join(",")
      return (
        <label
          htmlFor={`doc-file-${typeId}-${f.key}`}
          className="ev-doc-file pointer mb-0"
          title={name || "Upload"}
        >
          <input
            id={`doc-file-${typeId}-${f.key}`}
            type="file"
            accept={accept || undefined}
            className="d-none"
            onChange={e => setField(typeId, f.key, e.target.files?.[0] || null)}
          />
          <UploadArrowIcon />
          <span className="ms-2 text-truncate">
            {name || (f.file_types ? `Upload (${f.file_types})` : "Upload file")}
          </span>
          {!isFile && typeof v === "string" && v ? (
            <a
              href={`${baseUrl}${v}`}
              target="_blank"
              rel="noreferrer"
              className="ms-2 small"
              onClick={e => e.stopPropagation()}
            >
              view
            </a>
          ) : null}
        </label>
      )
    }
    default:
      return (
        <Input
          value={v ?? ""}
          onChange={e => setField(typeId, f.key, e.target.value)}
        />
      )
  }
}

export default EntityDocuments
